// Kudos and quirks: the copy that makes people smile. Rounds' voice: a wry, confident registrar on a busy shift. Short,
// specific, medical, with a twist; never a punchline for its own sake, never at the player's expense. No emoji, no
// exclamation marks. A line never restates what its screen already shows: the headline, the points, the stamp, the
// rank and movement on the board, or the room's split on the answers. Lines are picked deterministically (player +
// question), so a line never changes when the screen re-renders.

export type Pool = readonly string[];

export const HEADLINES = {
  correct: ['Correct', 'Spot on', 'Bang on', 'Nailed it', 'Textbook', 'Yes, doctor'],
  wrong: ['Not this time', 'Not quite', 'Close, but no', 'Wrong ward', 'Missed it'],
  none: ['No answer', 'Bleep missed', 'Time ran out'],
} as const;

export const POOLS = {
  lightning: [
    'Faster than a crash bleep.',
    'Reflexes of an A&E reg on nights.',
    'Quicker than a triage nurse.',
    'Lightning hands. Steady mind.',
    'Diagnosed from the doorway.',
    'Blink and you missed it.',
  ],
  quick: [
    'Sharp as a fresh scalpel.',
    'Clean as a sterile field.',
    'That is a pass with distinction.',
    'Smooth. Like a first-time cannula.',
    'Somebody did the reading.',
    'The registrar is quietly impressed.',
  ],
  steady: [
    'Measured. Your consultant would approve.',
    'Thought it through. Got it right. That is medicine.',
    'First, do no harm. Then, get it right.',
    'Safe hands.',
    'No panic. Just the right answer.',
    'Took a breath, made the call.',
  ],
  buzzer: [
    'Cutting it fine. Just inside the four hours.',
    'Buzzer beater.',
    'Just made the bleep.',
    'Last-second save. Very dramatic.',
    'Living dangerously. Still right.',
    'Photo finish with the timer.',
  ],
  // Most of the room missed it: the answers show by how much, so the line only reacts.
  rare: [
    'Hardly anyone got that. Show-off.',
    'The room went one way. You went the right way.',
    'Rare knowledge unlocked.',
    'Most of the room missed that. Not you.',
    'Elite company on that one.',
  ],
  firstBlood: [
    'Strong start. Do not peak too early.',
    'First one in the bag.',
    'Off the mark. First patient seen.',
  ],
  comeback: [
    'Back in the game.',
    'And we are back. Normal sinus rhythm restored.',
    'Shock delivered. Output restored.',
    'Never in doubt. Well, slightly in doubt.',
  ],
  wrongFast: [
    'Bold. Misguided, but bold.',
    'Speed is not everything. Ask any surgeon.',
    'Shot first, asked questions never.',
    'Confidence high. Accuracy pending.',
  ],
  wrongSlow: [
    'The next one is yours.',
    'Even consultants slip once a day.',
    'Shake it off. Next bleep incoming.',
    'You will remember this one forever now.',
    'A learning opportunity, as consultants say.',
    'That one is going in the revision pile.',
  ],
  wrongCommon: [
    'Tough one. The room struggled too.',
    'You were in good company on that one.',
    'The room fell for it too. Sneaky question.',
  ],
  streakLost: [
    'Streak ended at {n}. Respect the run.',
    'And the streak flatlines at {n}.',
    'That was a {n}-question streak. Gone, but not forgotten.',
  ],
  // A blank leaves the streak alone and scores nothing; a guess can score (shared/scoring.ts).
  noAnswer: [
    'A guess might have scored. A blank never does.',
    'Called away to theatre? Catch the next one.',
    'Phone face down? The next one is on its way.',
  ],
} as const satisfies Record<string, Pool>;

/** The streak bonus starts with the second right answer in a row (shared/scoring.ts streakBonus). */
export const STREAK = {
  2: ['Two in a row. Streak bonus on.'],
  3: ['Three in a row. On fire.', 'Hat-trick.'],
  4: ['Four straight. Unstoppable.', 'Four straight. Rock solid.'],
  5: ['Five in a row. Someone call the Royal College.', 'Five straight. Frightening.'],
  6: ['{n} in a row. This is getting silly.', '{n} straight. Save some for the rest of us.'],
  8: ['{n} straight. Are you the question writer?', '{n} in a row. We are checking your sleeves.'],
} as const;

/** The line under the board. The rows already show every rank and movement, so no line repeats a number from them. */
export const BOARD = {
  leader: ['Everyone is chasing you now.', 'Nice view from up here. Do not get cocky.'],
  climbBig: ['Climbing fast. Is this a comeback story?', 'Rocketing up the list.', 'That is a serious climb.'],
  climb: ['Moving up the list.', 'Momentum. Keep pushing.', 'Trending up, like a good obs chart.'],
  drop: ['Plenty of questions left.', 'A blip on the trace. Shake it off.', 'A dip, not a crash.'],
  hold: ['Stable. Observations unchanged.', 'Holding your ground.'],
  chasing: ['{name} is {gap} points ahead. Hunt them down.', '{gap} points to catch {name}.', '{name} is within reach. {gap} points.'],
  photoFinish: ['Photo finish. {gap} points in it.', 'Neck and neck. {gap} points.'],
} as const;

export const HIGHLIGHTS = {
  climber: 'Biggest climber: {name} ▲{n}',
  fastest: 'Fastest finger: {name} · {s} s',
  streak: 'Longest streak: {name} · {n}',
  photo: 'Photo finish for {place}: {gap} points',
  // When that player's row is already on the screen, its badge or flame carries the number.
  climberName: 'Biggest climber: {name}',
  streakName: 'Longest streak: {name}',
} as const;

/** Under a podium place (the stage) and on a result card: never the place itself, which is shown beside them. */
export const PODIUM = {
  1: ['Consultant material.', 'Top of the class. The consultant wants a word, a nice one.'],
  2: ['The registrar of the podium.', 'Silver, and well earned.'],
  3: ['Bronze. Still on the podium.', 'Very respectable. Very medal-shaped.'],
  top10: ['Strong finish. Rematch?', 'Respectable. The podium is next.'],
  rest: ['Every battle is practice for the real thing. Rematch?', 'The next battle is yours.', 'Revision pile updated. Rematch?'],
} as const;

/** Under the intro card's stamp ("Double points", "Lightning round", "Final question"), so never the stamp again. */
export const INTROS = {
  double: ['Measure twice, cut once.', 'Read it twice. Answer once.'],
  lightning: ['Trust your gut.', 'First instinct, final answer.'],
  final: ['Everything to play for.', 'Leave nothing in the tank.'],
  halfway: ['Half-time handover.', 'Halfway down the list.'],
} as const;

/** How to play, each true to shared/scoring.ts and the engine. Plain rules with character; no two say the same thing. */
export const LOBBY_TIPS = [
  'Your first tap is your answer.',
  "Speed scores, but only when you're right.",
  'Two right in a row starts a streak bonus. A wrong answer ends it.',
  'A blank never scores. A guess might.',
  'Dropped out? The same link brings you back, score intact.',
] as const;

export const DIFFICULTY = { easy: 'Warm-up', medium: 'Bread and butter', hard: "Consultant's question" } as const;

// ---------------------------------------------------------------------------
// Picking
// ---------------------------------------------------------------------------

/** FNV-1a hash of a string, for deterministic choices. */
export function hash(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 0x01000193) >>> 0; }
  return h;
}
export function pick(pool: Pool, seed: string): string { return pool[hash(seed) % pool.length]; }
export function fill(template: string, vars: Record<string, string | number>): string {
  return template.replace(/\{(\w+)\}/g, (_, k) => (k in vars ? String(vars[k]) : `{${k}}`));
}
export function ordinal(n: number): string { const s = ['th', 'st', 'nd', 'rd'], v = n % 100; return `${n}${s[(v - 20) % 10] ?? s[v] ?? s[0]}`; }

export interface RevealContext {
  playerId: string; questionIndex: number;
  answered: boolean; correct: boolean;
  elapsedMs?: number; durationMs: number;
  streakAfter: number;        // streak after this question (0 when wrong)
  streakBefore: number;       // streak going into this question
  wrongRunBefore: number;     // consecutive wrong or missed answers before this question
  roomCorrectPct?: number;    // 0–100, from the reveal counts
}
export interface RevealCopy { headline: string; line: string; chip?: string }

/** Headline, kudos line and an optional streak chip for the reveal screen. */
export function revealCopy(c: RevealContext): RevealCopy {
  const seed = `${c.playerId}:${c.questionIndex}`;
  const pct = c.roomCorrectPct === undefined ? undefined : Math.round(c.roomCorrectPct);
  if (!c.answered) return { headline: pick(HEADLINES.none, seed), line: pick(POOLS.noAnswer, seed + 'n'), chip: c.streakBefore >= 2 ? fill(pick(POOLS.streakLost, seed + 's'), { n: c.streakBefore }) : undefined };
  if (!c.correct) {
    const fast = c.elapsedMs !== undefined && c.elapsedMs < c.durationMs * 0.3;
    const pool = pct !== undefined && pct < 30 ? POOLS.wrongCommon : fast ? POOLS.wrongFast : POOLS.wrongSlow;
    return { headline: pick(HEADLINES.wrong, seed), line: fill(pick(pool, seed + 'w'), { pct: pct ?? 0 }), chip: c.streakBefore >= 2 ? fill(pick(POOLS.streakLost, seed + 's'), { n: c.streakBefore }) : undefined };
  }
  const f = (c.elapsedMs ?? c.durationMs) / c.durationMs;
  let pool: Pool = f < 0.25 ? POOLS.lightning : f < 0.5 ? POOLS.quick : f < 0.85 ? POOLS.steady : POOLS.buzzer;
  if (pct !== undefined && pct < 35) pool = POOLS.rare;
  else if (c.wrongRunBefore >= 2) pool = POOLS.comeback;
  else if (c.questionIndex === 0) pool = POOLS.firstBlood;
  return { headline: pick(HEADLINES.correct, seed), line: fill(pick(pool, seed + 'c'), { pct: pct ?? 0 }), chip: streakChip(c.streakAfter, seed) };
}

export function streakChip(n: number, seed: string): string | undefined {
  if (n < 2) return undefined;
  const key = n >= 8 ? 8 : n >= 6 ? 6 : (n as 2 | 3 | 4 | 5);
  return fill(pick(STREAK[key], seed + 'k'), { n });
}

export interface BoardContext { playerId: string; questionIndex: number; rank?: number; prevRank?: number; players: number; gapToAbove?: number; nameAbove?: string }
/** The one line under your row on the leaderboard. */
export function boardLine(c: BoardContext): string | undefined {
  if (!c.rank) return undefined;
  const seed = `${c.playerId}:${c.questionIndex}:b`;
  if (c.rank === 1) return pick(BOARD.leader, seed);
  if (c.gapToAbove !== undefined && c.gapToAbove <= 50 && c.nameAbove) return fill(pick(BOARD.photoFinish, seed), { gap: c.gapToAbove });
  const delta = c.prevRank ? c.prevRank - c.rank : 0;
  if (delta >= 5) return fill(pick(BOARD.climbBig, seed), { n: delta });
  if (delta > 0) return fill(pick(BOARD.climb, seed), { n: delta });
  if (delta < 0) return fill(pick(BOARD.drop, seed), { n: -delta });
  if (c.gapToAbove !== undefined && c.nameAbove && c.gapToAbove <= 400) return fill(pick(BOARD.chasing, seed), { name: c.nameAbove, gap: c.gapToAbove });
  return fill(pick(BOARD.hold, seed), { rank: ordinal(c.rank) });
}

export function podiumLine(rank: number, players: number, seed: string): string {
  if (rank === 1 || rank === 2 || rank === 3) return pick(PODIUM[rank], seed);
  if (rank <= 10) return pick(PODIUM.top10, seed);
  const pct = Math.max(1, Math.ceil((100 * rank) / Math.max(1, players)));
  return fill(pick(PODIUM.rest, seed), { rank: ordinal(rank), n: players, pct });
}

export function difficultyLabel(d?: string): string | undefined { return d && d in DIFFICULTY ? DIFFICULTY[d as keyof typeof DIFFICULTY] : undefined; }
